import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Search, Filter, Clock, BookOpen, ArrowRight, Sparkles } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { courses } from '../lib/data';
export function CoursesPage() {
  const [filter, setFilter] = useState('All');
  const categories = ['All', 'HACCP', 'Allergens', 'Management'];
  const filteredCourses = filter === 'All' ? courses : courses.filter(c => c.category === filter);
  return <div className="min-h-screen bg-gradient-to-br from-cream-100 via-white to-forest-50" data-id="element-529">
      {/* Header Section - Bold Design */}
      <div className="relative bg-gradient-to-br from-forest-900 via-forest-800 to-forest-900 text-white overflow-hidden" data-id="element-530">
        <div className="absolute inset-0 bg-mesh opacity-20" data-id="element-531"></div>
        <div className="absolute top-0 right-0 w-96 h-96 bg-ember-500/20 rounded-full blur-3xl" data-id="element-532"></div>
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-forest-500/20 rounded-full blur-3xl" data-id="element-533"></div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20" data-id="element-534">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6
        }} data-id="element-535">
            <Badge variant="warning" className="mb-6" data-id="element-536">
              <Sparkles className="w-3 h-3 mr-1" data-id="element-537" />
              Certified Training Programs
            </Badge>
            <h1 className="text-4xl md:text-6xl font-display font-bold mb-6" data-id="element-538">
              Our Training <span className="text-ember-400" data-id="element-539">Courses</span>
            </h1>
            <p className="text-xl text-forest-200 max-w-2xl leading-relaxed" data-id="element-540">
              Comprehensive food safety certification designed for Ireland's
              hospitality industry. FSAI compliant, engaging, and
              results-driven.
            </p>
          </motion.div>
        </div>
      </div>

      {/* Filters & Grid */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16" data-id="element-541">
        {/* Filter Bar */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-12 gap-6" data-id="element-542">
          <div className="flex flex-wrap gap-3" data-id="element-543">
            {categories.map(cat => <button key={cat} onClick={() => setFilter(cat)} className={`px-6 py-3 rounded-full text-sm font-bold transition-all duration-300 ${filter === cat ? 'bg-gradient-to-r from-forest-700 to-forest-600 text-white shadow-lg scale-105' : 'bg-white text-gray-700 border-2 border-forest-200 hover:border-forest-400 hover:shadow-md'}`} data-id="element-544">
                {cat}
              </button>)}
          </div>

          <div className="relative w-full md:w-80" data-id="element-545">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" data-id="element-546" />
            <input type="text" placeholder="Search courses..." className="w-full pl-12 pr-4 py-3 rounded-full border-2 border-forest-200 focus:outline-none focus:ring-2 focus:ring-forest-500 focus:border-transparent text-sm font-medium bg-white shadow-sm" data-id="element-547" />
          </div>
        </div>

        {/* Course Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-548">
          {filteredCourses.map((course, index) => <motion.div key={course.id} initial={{
          opacity: 0,
          y: 30
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: index * 0.1,
          duration: 0.5
        }} data-id="element-549">
              <Link to={`/courses/${course.id}`} className="block h-full group" data-id="element-550">
                <article className="bg-white rounded-3xl border-2 border-forest-100 overflow-hidden hover:border-forest-300 hover:shadow-2xl transition-all duration-500 h-full flex flex-col hover:-translate-y-2" data-id="element-551">
                  {/* Accent Bar */}
                  <div className="h-2 bg-gradient-to-r from-forest-500 via-ember-500 to-forest-500" data-id="element-552"></div>

                  <div className="p-8 flex-1 flex flex-col" data-id="element-553">
                    <div className="flex justify-between items-start mb-6" data-id="element-554">
                      <Badge variant={course.category === 'HACCP' ? 'success' : 'default'} data-id="element-555">
                        {course.category}
                      </Badge>
                      {course.level && <span className="text-xs font-bold text-forest-700 bg-forest-100 px-3 py-1 rounded-full" data-id="element-556">
                          {course.level}
                        </span>}
                    </div>

                    <h3 className="text-2xl font-display font-bold text-forest-900 mb-3 group-hover:text-forest-700 transition-colors" data-id="element-557">
                      {course.title}
                    </h3>
                    <p className="text-gray-600 text-sm mb-6 line-clamp-2 leading-relaxed" data-id="element-558">
                      {course.shortDescription}
                    </p>

                    <div className="mt-auto space-y-3" data-id="element-559">
                      <div className="flex items-center text-sm text-gray-600 font-medium" data-id="element-560">
                        <Clock className="w-4 h-4 mr-2 text-forest-600" data-id="element-561" />
                        {course.duration}
                      </div>
                      <div className="flex items-center text-sm text-gray-600 font-medium" data-id="element-562">
                        <BookOpen className="w-4 h-4 mr-2 text-forest-600" data-id="element-563" />
                        {course.delivery.join(', ')}
                      </div>
                    </div>
                  </div>

                  <div className="bg-gradient-to-r from-forest-50 to-cream-50 px-8 py-5 border-t-2 border-forest-100 flex justify-between items-center group-hover:from-forest-100 group-hover:to-ember-50 transition-all" data-id="element-564">
                    <span className="text-2xl font-display font-bold text-forest-900" data-id="element-565">
                      {course.price}
                    </span>
                    <span className="text-sm font-bold text-forest-700 group-hover:text-ember-600 transition-colors flex items-center" data-id="element-566">
                      View Details
                      <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-2 transition-transform" data-id="element-567" />
                    </span>
                  </div>
                </article>
              </Link>
            </motion.div>)}
        </div>

        {/* CTA Section */}
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="mt-20 bg-gradient-to-br from-forest-900 to-forest-800 rounded-3xl p-12 text-center text-white relative overflow-hidden" data-id="element-568">
          <div className="absolute inset-0 bg-mesh opacity-20" data-id="element-569"></div>
          <div className="absolute top-0 right-0 w-64 h-64 bg-ember-500/20 rounded-full blur-3xl" data-id="element-570"></div>

          <div className="relative z-10" data-id="element-571">
            <h2 className="text-3xl md:text-4xl font-display font-bold mb-4" data-id="element-572">
              Need Training for Your Team?
            </h2>
            <p className="text-forest-200 text-lg mb-8 max-w-2xl mx-auto" data-id="element-573">
              Get a custom quote for corporate training packages. Volume
              discounts available.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-574">
              <Button size="lg" variant="secondary" data-id="element-575">
                Request Corporate Quote
              </Button>
              <Button size="lg" variant="outline" className="text-white border-white hover:bg-white hover:text-forest-900" data-id="element-576">
                Download Brochure
              </Button>
            </div>
          </div>
        </motion.div>
      </div>
    </div>;
}